import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    detectPlatform,
    isMobileDevice,
    parseReferralData,
    buildCustomSchemeUrl,
    getStoreUrl,
    isInAppBrowser,
} from '../utils/deepLinkUtils';
import './DeepLinkHandler.css';

/**
 * DeepLinkHandler Component
 * Handles deep linking logic:
 * - Detects platform (iOS/Android/Desktop)
 * - Attempts to open native app via custom scheme
 * - Falls back to App Store/Google Play if app not installed
 * - Redirects desktop users to home page
 *
 * @param {Object} props
 * @param {string} props.type - Type of deep link (referral, profile, food)
 */
const DeepLinkHandler = ({ type }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [platform, setPlatform] = useState(null);
    const [status, setStatus] = useState('detecting'); // detecting, launching, redirecting, attempting
    const [linkData, setLinkData] = useState(null);
    const [autoOpenAttempted, setAutoOpenAttempted] = useState(false);

    useEffect(() => {
        // Detect platform and parse URL data
        const detectedPlatform = detectPlatform();
        const parsedData = parseReferralData(location.pathname, location.search);

        setPlatform(detectedPlatform);
        setLinkData(parsedData);

        console.log('Deep Link Debug:', {
            platform: detectedPlatform,
            linkData: parsedData,
            pathname: location.pathname,
            search: location.search,
        });

        // Handle deep linking logic
        if (isMobileDevice()) {
            handleMobileDeepLink(detectedPlatform, parsedData);
        } else {
            handleDesktopRedirect(parsedData);
        }

        // Cleanup function
        return () => {
            // Remove event listeners if any were added
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    /**
     * Handle deep linking for mobile devices
     */
    const handleMobileDeepLink = (detectedPlatform, parsedData) => {
        console.log('Mobile device detected:', detectedPlatform);
        console.log('Parsed data:', JSON.stringify(parsedData, null, 2));

        // Check for in-app browser
        const inAppBrowser = isInAppBrowser();
        if (inAppBrowser) {
            console.warn('In-app browser detected - Universal Links may not work properly');
        }
        // Show buttons directly - more reliable for iOS
        // Automatic redirect via JavaScript is often blocked in modern iOS
        setStatus('launching');
        // For Android, we can try automatic open
        if (detectedPlatform === 'android' && !inAppBrowser) {
            attemptAutoOpenAndroid(parsedData);
        }
    };

    /**
     * Attempt to automatically open the app on iOS
     */

    /**
     * Attempt to automatically open the app on Android
     */
    const attemptAutoOpenAndroid = (parsedData) => {
        console.log('Android: Attempting automatic app open');
        setAutoOpenAttempted(true);

        const customSchemeUrl = buildCustomSchemeUrl(parsedData);

        // Try direct navigation
        window.location.href = customSchemeUrl;

        // Fallback: if still here after 1.5 seconds, show buttons
        setTimeout(() => {
            console.log('Android: App might not be installed, showing options');
            setStatus('launching');
        }, 1500);
    };

    /**
     * Handle redirect for desktop users
     */
    const handleDesktopRedirect = (parsedData) => {
        console.log('Desktop detected, redirecting to home');

        // Save link data to location state for potential use
        setTimeout(() => {
            navigate('/', {
                state: {
                    from: 'deeplink',
                    linkData: parsedData,
                },
            });
        }, 1000);
    };

    /**
     * Get the app open URL based on platform
     * Use custom scheme for both iOS and Android
     */
    const getAppOpenUrl = () => {
        // Use custom scheme for both platforms
        return buildCustomSchemeUrl(linkData);
    };

    /**
     * Render appropriate UI based on status
     */
    const renderContent = () => {
        if (!platform || !linkData) {
            return (
                <div className="deeplink-content">
                    <div className="deeplink-spinner"></div>
                    <p>Detectando plataforma...</p>
                </div>
            );
        }

        if (status === 'detecting') {
            return (
                <div className="deeplink-content">
                    <div className="deeplink-spinner"></div>
                    <p>Preparando...</p>
                </div>
            );
        }

        if (status === 'attempting') {
            return (
                <div className="deeplink-content">
                    <div className="deeplink-spinner"></div>
                    <div className="deeplink-icon">📱</div>
                    <h2>Abriendo JAMA...</h2>
                    <p className="deeplink-message">
                        {platform === 'ios'
                            ? 'Intentando abrir la app automáticamente...'
                            : 'Redirigiendo a la app JAMA...'
                        }
                    </p>
                </div>
            );
        }

        if (status === 'launching') {
            const appOpenUrl = getAppOpenUrl();
            const storeUrl = getStoreUrl(platform, linkData);

            // Handler for manual app open
            const handleManualOpen = (e) => {
                e.preventDefault();
                console.log('Manual open clicked. URL:', appOpenUrl);

                if (platform === 'ios') {
                    // For iOS: Use window.location.assign
                    console.log('iOS: Trying to open app with custom scheme');
                    try {
                        window.location.assign(appOpenUrl);

                        // Fallback after 2 seconds if still here
                        setTimeout(() => {
                            console.log('iOS: App might not be installed');
                        }, 2000);
                    } catch (err) {
                        console.error('iOS: Error opening app:', err);
                    }
                } else {
                    // For Android: Use custom scheme
                    console.log('Android: Using custom scheme');
                    window.location.href = appOpenUrl;
                }
            };

            return (
                <div className="deeplink-content">
                    <div className="deeplink-icon">📱</div>
                    <h2>{autoOpenAttempted ? '¿No se abrió la app?' : '¿Tienes la app JAMA?'}</h2>
                    <p className="deeplink-message">
                        {autoOpenAttempted
                            ? 'Toca el botón para intentar de nuevo'
                            : platform === 'ios'
                            ? 'Toca el botón para abrir JAMA'
                            : 'Si tienes la app instalada, ábrela para continuar'
                        }
                    </p>

                    {/* Use both href and onClick for better compatibility */}
                    <a
                        href={appOpenUrl}
                        onClick={handleManualOpen}
                        className="deeplink-button deeplink-button-primary"
                        style={{ textDecoration: 'none', display: 'inline-block' }}
                    >
                        Abrir JAMA App
                    </a>

                    <p className="deeplink-divider">o</p>

                    <a
                        href={storeUrl}
                        className="deeplink-button deeplink-button-secondary"
                        style={{ textDecoration: 'none', display: 'inline-block' }}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Descargar desde {platform === 'ios' ? 'App Store' : 'Google Play'}
                    </a>
                </div>
            );
        }

        if (status === 'redirecting') {
            return (
                <div className="deeplink-content">
                    <div className="deeplink-spinner"></div>
                    <div className="deeplink-icon">🏪</div>
                    <h2>Redirigiendo a la tienda...</h2>
                    <p>Descarga JAMA para continuar</p>
                </div>
            );
        }

        return (
            <div className="deeplink-content">
                <div className="deeplink-spinner"></div>
                <p>Procesando...</p>
            </div>
        );
    };

    return (
        <div className="deeplink-container">
            {renderContent()}

            {/* Debug info (remove in production) */}
            {process.env.NODE_ENV === 'development' && linkData && (
                <div className="deeplink-debug">
                    <h4>Debug Info:</h4>
                    <p><strong>Platform:</strong> {platform}</p>
                    <p><strong>Type:</strong> {linkData.type || type}</p>
                    <p><strong>Code:</strong> {linkData.code || 'N/A'}</p>
                    <p><strong>User ID:</strong> {linkData.userId || 'N/A'}</p>
                    <p><strong>Username:</strong> {linkData.username || 'N/A'}</p>
                    <p><strong>Restaurant ID:</strong> {linkData.restaurantId || 'N/A'}</p>
                    <p><strong>Status:</strong> {status}</p>
                    <p><strong>Auto-open attempted:</strong> {autoOpenAttempted ? 'Yes' : 'No'}</p>
                    <p><strong>App URL:</strong> {getAppOpenUrl()}</p>
                </div>
            )}
        </div>
    );
};

export default DeepLinkHandler;
